import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, Loader2, FileMinus, Printer, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/jobCalc";

export const AdminCreditNotes = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try { setRows(await adminApi.listCreditNotes()); }
    catch { toast.error("Could not load credit notes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this credit note?")) return;
    try { await adminApi.deleteCreditNote(id); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const total = rows.reduce((a, r) => a + (r.gross || 0), 0);

  return (
    <div data-testid="admin-credit-notes-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Credit notes</h1>
          <p className="text-[#3f4b52] mt-1">Refunds, goodwill gestures and corrections against invoices.</p>
        </div>
        <Link
          to="/admin/credit-notes/new"
          data-testid="cn-new-btn"
          className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2"
        >
          <Plus size={14} /> New credit note
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-3">
        <SummaryCard label="Credit notes" value={rows.length} />
        <SummaryCard label="Total credits issued" value={money(total)} color="#f28a16" />
        <SummaryCard label="Linked to invoices" value={rows.filter((r) => r.job_id).length} />
      </div>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#3f4b52] inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#3f4b52]">
            <FileMinus size={32} className="mx-auto text-[#9aa6ad]" />
            <p className="mt-2">No credit notes yet. <Link to="/admin/credit-notes/new" className="text-[#f28a16] hover:underline">Create one →</Link></p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#edf2f4] text-[#3f4b52] uppercase tracking-widest text-xs">
                <tr>
                  <th className="text-left px-4 py-2.5">Number</th>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Customer</th>
                  <th className="text-left px-4 py-2.5">Description</th>
                  <th className="text-left px-4 py-2.5">Against invoice</th>
                  <th className="text-right px-4 py-2.5">Amount</th>
                  <th className="text-right px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef2f4]">
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => nav(`/admin/credit-notes/${r.id}`)} className="hover:bg-[#f5f6f6] cursor-pointer" data-testid={`cn-row-${r.number}`}>
                    <td className="px-4 py-3 font-mono font-bold text-[#f28a16]">{r.number}</td>
                    <td className="px-4 py-3 text-[#3f4b52]">{r.date}</td>
                    <td className="px-4 py-3">{r.customer_code || "—"}</td>
                    <td className="px-4 py-3 text-[#3f4b52] truncate max-w-[280px]">{r.description || r.reason || "—"}</td>
                    <td className="px-4 py-3 text-[#3f4b52] text-xs">{r.invoice_reference || <span className="italic text-[#9aa6ad]">Standalone</span>}</td>
                    <td className="px-4 py-3 text-right font-bold">{money(r.gross)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); remove(r.id); }} className="text-[#dc2626] text-xs hover:underline inline-flex items-center gap-1">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color = "#111619" }) => (
  <div className="bg-white border border-[#cfd7db] rounded-md p-4">
    <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">{label}</div>
    <div className="font-display font-black text-2xl mt-0.5" style={{ color }}>{value}</div>
  </div>
);

export default AdminCreditNotes;
